import { useState, useEffect, useCallback, useRef } from 'react'
import { X, ChevronDown, ChevronRight, Fingerprint, IdCard, Copy, Check } from 'lucide-react'
import { CredentialBar } from './CredentialBar'
import { GroupedTabBar } from './GroupedTabBar'
import { KycV2Workspace } from './KycV2Workspace'
import type { Creds, SavedConfig, LogEntry, TabId } from './types'
import { loadCredsFromStorage, saveCredsToStorage } from './utils'
import { DEFAULT_CREDS } from './data'
import { KycV1Workspace } from './KycV1Workspace'
import { KybV2Workspace } from './KybV2Workspace'
import { KybV1Workspace } from './KybV1Workspace'
import { HostedV2Workspace } from './HostedV2Workspace'
import { HostedV1Workspace } from './HostedV1Workspace'
import { EmbeddedWorkspace } from './EmbeddedWorkspace'
import { WebhooksWorkspace } from './WebhooksWorkspace'
import { ExplorerWorkspace } from './ExplorerWorkspace'
import { TestDataTab } from '../ApiConsole'
import { TestCaseGenerator } from '../TestCaseGenerator'
import { ImplTodoSection } from '../SequenceDiagramToolPage'

// ── Guide tab data ─────────────────────────────────────────────────────────────

type ResultType = 'PASS' | 'FAIL' | 'REVIEW' | 'HIT' | 'CLEAR' | 'ASYNC'

interface GuideStep { action: string; detail?: string }
interface GuideCase { id: string; title: string; description: string; result: ResultType; resultNote?: string; steps: GuideStep[] }
interface GuideScenario { label: string; icon: React.ElementType; color: string; bg: string; cases: GuideCase[] }

const HOSTED_BIOMETRICS_CASES: GuideCase[] = [
  { id: 'hosted-bio-new', title: 'New Customer — Biometrics', description: 'Generate Hosted OneSDK URL for a new customer. Customer completes document capture + face biometrics.', result: 'CLEAR', steps: [
    { action: 'In the FrankieOne Portal, navigate to Flows and copy your biometric Flow ID' },
    { action: 'Call POST /v2/individuals/hostedUrl', detail: 'Payload: { consentObtained: true, oneSDKFlowId: "<your-flow-id>", redirectUrl: "https://your-app.com/complete" }' },
    { action: 'Copy the url and entityId from the response' },
    { action: 'Open the url in a browser — the Hosted OneSDK loads' },
    { action: 'Complete document capture: photograph a valid ID' },
    { action: 'Complete face scan and liveness check' },
    { action: 'Wait for redirect to your redirectUrl — signals completion' },
    { action: 'Call GET /v2/individuals/{entityId}', detail: 'Expect: overallRiskLevel: LOW, biometrics PASS, score ≥ 0.7' },
  ]},
  { id: 'hosted-bio-face-mismatch', title: 'Face Mismatch — FAIL', description: 'Selfie does not match document photo. Biometric similarity below threshold.', result: 'FAIL', resultNote: 'Biometric mismatch', steps: [
    { action: 'Call POST /v2/individuals/hostedUrl (no entityId) — note the entityId from the response' },
    { action: 'Open the url in a browser' },
    { action: 'Complete document capture: photograph a real, valid ID document' },
    { action: 'At the selfie step: present a different person\'s face', detail: 'Options: hold up a phone showing someone else\'s photo, ask a colleague to take the selfie' },
    { action: 'Complete liveness with a real face' },
    { action: 'Call GET /v2/individuals/{entityId}', detail: 'Expect: biometrics FAIL, failReason: FACE_MISMATCH, score < 0.7' },
  ]},
  { id: 'hosted-bio-liveness', title: 'Liveness Failure — FAIL', description: 'Customer fails liveness detection. Anti-spoofing rejects printed photo or screen.', result: 'FAIL', resultNote: 'Liveness failure', steps: [
    { action: 'Call POST /v2/individuals/hostedUrl — note the entityId' },
    { action: 'Open the url in a browser' },
    { action: 'Complete document capture normally' },
    { action: 'At the face scan step: hold up a printed photo or screen instead of your real face', detail: 'The anti-spoofing system detects flat images — it will reject them' },
    { action: 'Allow all retry attempts to fail' },
    { action: 'Call GET /v2/individuals/{entityId}', detail: 'Expect: biometrics FAIL, failReason: LIVENESS_FAILURE' },
  ]},
]

const HOSTED_ID_FORM_CASES: GuideCase[] = [
  { id: 'hosted-form-new', title: 'New Customer — ID Form', description: 'Hosted URL for ID-form-only flow (no biometrics). Customer enters document details in the hosted UI.', result: 'ASYNC', resultNote: 'Check result after submit', steps: [
    { action: 'In the FrankieOne Portal, find your ID-form-only flow and copy its Flow ID' },
    { action: 'Call POST /v2/individuals/hostedUrl with the ID-form Flow ID', detail: 'Payload: { consentObtained: true, oneSDKFlowId: "<id-form-flow-id>", redirectUrl: "..." }' },
    { action: 'Note both the url and entityId from the response' },
    { action: 'Open the url in a browser — a hosted form loads' },
    { action: 'Fill in customer details: name, date of birth, document type and number, address' },
    { action: 'Submit the form and wait for redirect' },
    { action: 'Call GET /v2/individuals/{entityId}', detail: 'Use TESTONE persona data for CLEAR, TESTEIGHT for FAIL' },
  ]},
  { id: 'hosted-form-existing', title: 'Existing Customer — ID Form', description: 'ID form for existing customer — pass entityId to update their record in place.', result: 'ASYNC', resultNote: 'Check result after submit', steps: [
    { action: 'Get the entityId of the existing customer' },
    { action: 'Call POST /v2/individuals/hostedUrl with entityId included', detail: 'Payload: { consentObtained: true, oneSDKFlowId: "<id-form-flow-id>", redirectUrl: "...", entityId: "ent_existing_id" }' },
    { action: 'Open the url — form is pre-linked to the existing entity' },
    { action: 'Fill in and submit the form' },
    { action: 'Call GET /v2/individuals/{entityId}', detail: 'Entity updated in place — no duplicate created' },
  ]},
]

const EMBEDDED_BIOMETRICS_CASES: GuideCase[] = [
  { id: 'emb-bio-execute', title: 'Execute Workflow (Server-side)', description: 'Backend executes workflow to create entity and get entityId. Pass to OneSDK in your frontend.', result: 'ASYNC', resultNote: 'SDK handles capture', steps: [
    { action: 'From your backend, call POST /v2/individuals/new/serviceprofiles/{serviceProfileId}/workflows/{workflowId}/execute', detail: 'Payload: { individual: { name: { givenName, familyName }, dateOfBirth: { year, month, day }, addresses: [...] } }' },
    { action: 'Copy the entityId from the response' },
    { action: 'Pass entityId to your frontend OneSDK: new OneSdk({ entityId: "ent_xxxx", ... })' },
    { action: 'Guide the customer through document capture, face scan, and liveness' },
    { action: 'After onSuccess fires, call GET /v2/individuals/{entityId}', detail: 'Expect: overallRiskLevel: LOW, biometrics PASS, score ≥ 0.7' },
  ]},
  { id: 'emb-bio-face-mismatch', title: 'Face Mismatch — FAIL', description: 'Embedded SDK biometric fails. Poll result endpoint to see FAIL outcome.', result: 'FAIL', resultNote: 'Biometric mismatch', steps: [
    { action: 'Initialise the Embedded OneSDK with a fresh entityId from a workflow execute call' },
    { action: 'Complete document capture: photograph a real, valid ID document' },
    { action: 'At the selfie step: use a different person\'s face — not matching the ID document' },
    { action: 'After SDK completion, call GET /v2/individuals/{entityId}', detail: 'Expect: biometrics FAIL, failReason: FACE_MISMATCH, score < 0.7' },
  ]},
]

const EMBEDDED_ID_FORM_CASES: GuideCase[] = [
  { id: 'emb-form-testone', title: 'ID Form — TESTONE — CLEAR', description: 'Execute KYC workflow server-side with JAMES A TESTONE (no biometrics). Expected: CLEAR.', result: 'CLEAR', steps: [
    { action: 'Call POST /v2/individuals/new/serviceprofiles/{id}/workflows/{id}/execute' },
    { action: 'Use payload: givenName: JAMES, middleName: A, familyName: TESTONE, DOB: 1950-01-01, address: 35 Conn St, Ferntree Gully VIC 3156' },
    { action: 'Note the entityId from the response' },
    { action: 'Call GET /v2/individuals/{entityId}', detail: 'Expect: kyc_name_dob: CLEAR, overallRiskLevel: LOW' },
  ]},
  { id: 'emb-form-testeight', title: 'ID Form — TESTEIGHT — FAIL', description: 'Execute workflow with JENNY TESTEIGHT. No records found in any source.', result: 'FAIL', steps: [
    { action: 'Call POST /v2/individuals/new/serviceprofiles/{id}/workflows/{id}/execute' },
    { action: 'Use payload: givenName: JENNY, familyName: TESTEIGHT, DOB: 1957-01-01, address: 56 Victoria St, Fingal TAS 7214' },
    { action: 'Note the entityId from the response' },
    { action: 'Call GET /v2/individuals/{entityId}', detail: 'Expect: kyc_name_dob: FAIL, overallRiskLevel: HIGH — TESTEIGHT has no records in any verification source' },
  ]},
]

const GUIDE_SCENARIOS: GuideScenario[] = [
  { label: 'Hosted SDK — Biometrics', icon: Fingerprint, color: 'text-violet-400', bg: 'bg-violet-500/10', cases: HOSTED_BIOMETRICS_CASES },
  { label: 'Hosted SDK — ID Form', icon: IdCard, color: 'text-blue-400', bg: 'bg-blue-500/10', cases: HOSTED_ID_FORM_CASES },
  { label: 'Embedded SDK — Biometrics', icon: Fingerprint, color: 'text-pink-400', bg: 'bg-pink-500/10', cases: EMBEDDED_BIOMETRICS_CASES },
  { label: 'Embedded SDK — ID Form', icon: IdCard, color: 'text-orange-400', bg: 'bg-orange-500/10', cases: EMBEDDED_ID_FORM_CASES },
]

const RESULT_STYLES: Record<ResultType, string> = {
  PASS: 'bg-green-500/15 text-green-400 border-green-500/30',
  FAIL: 'bg-red-500/15 text-red-400 border-red-500/30',
  REVIEW: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  HIT: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  CLEAR: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  ASYNC: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
}

function GuideCaseRow({ tc, idx }: { tc: GuideCase; idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <tr
        className={`border-t border-[var(--border-color)] cursor-pointer transition-colors ${open ? 'bg-[var(--bg-tertiary)]' : idx % 2 === 0 ? 'bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)]' : 'bg-[var(--bg-secondary)]/60 hover:bg-[var(--bg-tertiary)]'}`}
        onClick={() => setOpen(!open)}
      >
        <td className="px-3 py-2 w-4">
          {open ? <ChevronDown size={12} className="text-[var(--text-muted)]" /> : <ChevronRight size={12} className="text-[var(--text-muted)]" />}
        </td>
        <td className="px-2 py-2 text-xs text-[var(--text-primary)] font-medium">{tc.title}</td>
        <td className="px-2 py-2">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border whitespace-nowrap ${RESULT_STYLES[tc.result]}`}>
            {tc.result}{tc.resultNote && <span className="font-normal opacity-75 hidden sm:inline"> · {tc.resultNote}</span>}
          </span>
        </td>
        <td className="px-2 py-2 text-[11px] text-[var(--text-muted)] max-w-[320px] hidden lg:table-cell">{tc.description}</td>
      </tr>
      {open && (
        <tr className="border-t border-[var(--border-color)]">
          <td colSpan={4} className="p-4 bg-[var(--bg-primary)]">
            <ol className="space-y-2.5">
              {tc.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[10px] font-bold text-[var(--text-muted)] flex items-center justify-center mt-0.5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-[var(--text-primary)]">{step.action}</p>
                    {step.detail && <p className="text-[10px] text-[var(--text-muted)] mt-0.5 italic">{step.detail}</p>}
                  </div>
                </li>
              ))}
            </ol>
          </td>
        </tr>
      )}
    </>
  )
}

// ── Log drawer ─────────────────────────────────────────────────────────────────

function MethodBadge({ method }: { method: string }) {
  const cls = method === 'GET' ? 'bg-sky-500/15 text-sky-300' : method === 'POST' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-gray-500/15 text-gray-300'
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold shrink-0 ${cls}`}>{method}</span>
}

function HttpBadge({ code }: { code: number }) {
  const cls = code === 202 ? 'bg-amber-500/15 text-amber-300' : code >= 200 && code < 300 ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold shrink-0 ${cls}`}>{code}</span>
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { void navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] transition-colors hover:bg-[#1a2740]"
      style={{ color: copied ? '#34d399' : '#5b6b85' }}
    >
      {copied ? <Check size={9} /> : <Copy size={9} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

function LogDrawer({ log, onClear, onClose }: { log: LogEntry[]; onClear: () => void; onClose: () => void }) {
  const [selected, setSelected] = useState<number | null>(null)

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 flex"
      style={{ animation: 'slideInRight .2s ease' }}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div
        className="relative ml-auto flex flex-col"
        style={{ width: 380, background: '#0d1626', borderLeft: '1px solid #1e2c44', zIndex: 1 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #1e2c44' }}>
          <span className="text-[12px] font-semibold text-[#94a3b8] uppercase tracking-wider">API Log</span>
          <div className="flex items-center gap-2">
            {log.length > 0 && (
              <button onClick={onClear} className="text-[11px] text-[#5b6b85] hover:text-red-400 transition-colors">Clear</button>
            )}
            <button onClick={onClose} className="text-[#5b6b85] hover:text-[#94a3b8]"><X size={14} /></button>
          </div>
        </div>

        {/* Log entries */}
        <div className="flex-1 overflow-y-auto">
          {log.length === 0 && <p className="px-4 py-6 text-[11px] text-[#475569] italic">No calls yet.</p>}
          {[...log].reverse().map(e => (
            <button
              key={e.id}
              onClick={() => setSelected(e.id === selected ? null : e.id)}
              className="w-full text-left px-4 py-2.5 transition-colors hover:bg-[#111c30]"
              style={{ borderBottom: '1px solid #1a2740', background: selected === e.id ? '#111c30' : undefined }}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <MethodBadge method={e.method} />
                <HttpBadge code={e.status} />
                <span className="text-[9px] text-[#5b6b85] ml-auto">{e.ts} · {e.durationMs}ms</span>
              </div>
              <p className="text-[10px] text-[#7e8ba6] truncate font-mono select-text">{e.url.replace(/https?:\/\/[^/]+/, '')}</p>
              <p className="text-[9px] text-[#3f4f6b] mt-0.5 italic truncate">{e.annotation}</p>

              {selected === e.id && (
                <div className="mt-2 space-y-2" onClick={ev => ev.stopPropagation()}>
                  {/* Full URL */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[9px] font-semibold text-[#5b6b85] uppercase">URL</p>
                      <CopyBtn text={e.url} />
                    </div>
                    <pre className="text-[9px] text-[#94a3b8] font-mono whitespace-pre-wrap break-all bg-[#0a1322] rounded p-2 border border-[#1e2c44] select-text cursor-text">
                      {e.method} {e.url}
                    </pre>
                  </div>
                  {e.requestBody !== undefined && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[9px] font-semibold text-[#5b6b85] uppercase">Request Body</p>
                        <CopyBtn text={JSON.stringify(e.requestBody, null, 2)} />
                      </div>
                      <pre className="text-[9px] text-[#94a3b8] font-mono whitespace-pre-wrap break-all bg-[#0a1322] rounded p-2 border border-[#1e2c44] select-text cursor-text">
                        {JSON.stringify(e.requestBody, null, 2)}
                      </pre>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[9px] font-semibold text-[#5b6b85] uppercase">Response</p>
                      <CopyBtn text={JSON.stringify(e.responseBody, null, 2)} />
                    </div>
                    <pre className="text-[9px] text-[#94a3b8] font-mono whitespace-pre-wrap break-all bg-[#0a1322] rounded p-2 border border-[#1e2c44] select-text cursor-text">
                      {JSON.stringify(e.responseBody, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── TODO popover ───────────────────────────────────────────────────────────────

function TodoModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end pt-16 pr-5">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div
        className="relative rounded-[14px] overflow-hidden shadow-2xl"
        style={{ width: 360, maxHeight: '80vh', background: '#0d1626', border: '1px solid #1e2c44', animation: 'fadeUp .15s ease', overflowY: 'auto' }}
      >
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #1e2c44' }}>
          <span className="text-[12px] font-semibold text-[#94a3b8]">Development TODO</span>
          <button onClick={onClose} className="text-[#5b6b85] hover:text-[#94a3b8]"><X size={13} /></button>
        </div>
        <div className="p-3">
          <ImplTodoSection context="api-testing" title="" />
        </div>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export function TestingGroundRedesign() {
  const [creds, setCreds] = useState<Creds>(() => loadCredsFromStorage(DEFAULT_CREDS))
  const [activeTab, setActiveTab] = useState<TabId>('kyc-v2')
  const [log, setLog] = useState<LogEntry[]>([])
  const logIdRef = useRef(0)
  const [editorOpen, setEditorOpen] = useState(false)
  const [logDrawerOpen, setLogDrawerOpen] = useState(false)
  const [todoOpen, setTodoOpen] = useState(false)
  const [savedConfigs, setSavedConfigs] = useState<SavedConfig[]>([])
  const [activeConfigId, setActiveConfigId] = useState<number | null>(null)

  // Load saved configs from server
  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch('/api/api-console/credentials')
        const json = await res.json() as { success: boolean; data: SavedConfig[] }
        if (json.success) setSavedConfigs(json.data)
      } catch { /* silent */ }
    })()
  }, [])

  function updateCreds(next: Creds) {
    setCreds(next)
    saveCredsToStorage(next)
  }

  async function handleSaveNew(name: string) {
    try {
      const res = await fetch('/api/api-console/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, customerId: creds.customerId, childCustomerId: creds.childCustomerId,
          apiKey: creds.apiKey, apiBase: creds.apiBase, machineBase: creds.machineBase,
        }),
      })
      const json = await res.json() as { success: boolean; data: { id: number } }
      // Refresh configs
      const listRes = await fetch('/api/api-console/credentials')
      const listJson = await listRes.json() as { success: boolean; data: SavedConfig[] }
      if (listJson.success) setSavedConfigs(listJson.data)
      if (json.success) setActiveConfigId(json.data.id)
    } catch { /* silent */ }
  }

  async function handleDeleteConfig(id: number) {
    try {
      await fetch(`/api/api-console/credentials/${id}`, { method: 'DELETE' })
      setSavedConfigs(prev => prev.filter(c => c.id !== id))
      if (activeConfigId === id) setActiveConfigId(null)
    } catch { /* silent */ }
  }

  function handleSelectConfig(id: number) {
    const cfg = savedConfigs.find(c => c.id === id)
    if (!cfg) return
    setActiveConfigId(id)
    updateCreds({
      customerId: cfg.customer_id,
      childCustomerId: cfg.child_customer_id ?? '',
      apiKey: cfg.api_key,
      apiBase: cfg.api_base,
      machineBase: cfg.machine_base,
    })
  }

  function handleClearConfig() {
    setActiveConfigId(null)
    updateCreds(DEFAULT_CREDS)
  }

  async function handleTestConnection(): Promise<'ok' | 'error'> {
    try {
      const res = await fetch(`/api/frankie-proxy/kyc/ruok`, {
        headers: {
          'X-FT-Customer-ID': creds.customerId,
          'X-FT-API-Key': creds.apiKey,
          'X-FT-API-Base': creds.apiBase,
        },
      })
      return res.ok ? 'ok' : 'error'
    } catch {
      return 'error'
    }
  }

  const addLog = useCallback((entry: Omit<LogEntry, 'id' | 'ts'>) => {
    logIdRef.current += 1
    const id = logIdRef.current
    const ts = new Date().toLocaleTimeString()
    setLog(prev => [...prev, { ...entry, id, ts }])
  }, [])

  return (
    // AppLayout sets max-w-none for this route, so no breakout needed.
    <div style={{ background: '#0a1322', minHeight: '100%' }}>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(7px) } to { opacity: 1; transform: none } }
        @keyframes slideInRight { from { transform: translateX(100%) } to { transform: translateX(0) } }
        @keyframes blip { 0%,100% { opacity: .35; transform: scale(.8) } 50% { opacity: 1; transform: scale(1.15) } }
      `}</style>

      <CredentialBar
        creds={creds}
        onChange={updateCreds}
        savedConfigs={savedConfigs}
        activeConfigId={activeConfigId}
        onSelectConfig={handleSelectConfig}
        onSaveNew={handleSaveNew}
        onDeleteConfig={handleDeleteConfig}
        onClearConfig={handleClearConfig}
        editorOpen={editorOpen}
        onToggleEditor={() => setEditorOpen(o => !o)}
        onTestConnection={handleTestConnection}
        onLogClick={() => setLogDrawerOpen(o => !o)}
        onTodoClick={() => setTodoOpen(o => !o)}
        logCount={log.length}
      />

      <GroupedTabBar activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab content */}
      <div className="px-5 pb-8">
        {activeTab === 'kyc-v2' && <KycV2Workspace creds={creds} addLog={addLog} />}
        {activeTab === 'kyc-v1' && <KycV1Workspace creds={creds} addLog={addLog} />}
        {activeTab === 'kyb-v2' && <KybV2Workspace creds={creds} addLog={addLog} />}
        {activeTab === 'kyb-v1' && <KybV1Workspace creds={creds} addLog={addLog} />}
        {activeTab === 'hosted-v2' && <HostedV2Workspace creds={creds} addLog={addLog} />}
        {activeTab === 'hosted-v1' && <HostedV1Workspace creds={creds} addLog={addLog} />}
        {activeTab === 'embedded' && <EmbeddedWorkspace creds={creds} addLog={addLog} />}
        {activeTab === 'webhooks' && <WebhooksWorkspace creds={creds} addLog={addLog} />}
        {activeTab === 'explorer' && <ExplorerWorkspace creds={creds} addLog={addLog} />}

        {activeTab === 'uat' && (
          <div className="pt-4">
            <TestCaseGenerator />
          </div>
        )}

        {activeTab === 'test-data' && (
          <div className="pt-4">
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Test Data Reference</h2>
              <p className="text-[11px] text-[var(--text-muted)] mt-0.5">FrankieOne UAT test personas — individuals and organizations.</p>
            </div>
            <TestDataTab />
          </div>
        )}

        {activeTab === 'guide' && (
          <div className="space-y-4 pt-4">
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Test Case Guide</h2>
              <p className="text-[11px] text-[var(--text-muted)] mt-0.5">Step-by-step instructions for common FrankieOne verification scenarios.</p>
            </div>
            {GUIDE_SCENARIOS.map(scenario => {
              const Icon = scenario.icon
              return (
                <div key={scenario.label} className="border border-[var(--border-color)] rounded-lg overflow-hidden bg-[var(--bg-secondary)]">
                  <div className={`flex items-center gap-2 px-3 py-2 border-b border-[var(--border-color)] ${scenario.bg}`}>
                    <Icon size={14} className={scenario.color} />
                    <span className={`text-xs font-semibold ${scenario.color}`}>{scenario.label}</span>
                    <span className="ml-auto text-[10px] text-[var(--text-muted)]">{scenario.cases.length} cases</span>
                  </div>
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[var(--bg-primary)]">
                        <th className="w-6" />
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Test Case</th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide w-28">Result</th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide hidden lg:table-cell">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scenario.cases.map((tc, idx) => <GuideCaseRow key={tc.id} tc={tc} idx={idx} />)}
                    </tbody>
                  </table>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Log drawer */}
      {logDrawerOpen && (
        <LogDrawer log={log} onClear={() => setLog([])} onClose={() => setLogDrawerOpen(false)} />
      )}

      {/* TODO modal */}
      {todoOpen && <TodoModal onClose={() => setTodoOpen(false)} />}
    </div>
  )
}
